import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'
import type {
  SourceIngestGuidance,
  SourceIngestPhase,
  SourcePurpose,
  StudioSourceKind,
} from './source-ingest-model'

interface SourceIngestCopy {
  status: string
  detectedAs: string
  purpose: string
  suggested: string
  purposeOptions: Record<SourcePurpose, string>
  phase: Record<SourceIngestPhase, string>
  sourceKind: Record<StudioSourceKind, string>
  provenance: string
  provenancePending: string
  provenanceValue: Record<SourceIngestGuidance['provenanceKey'], string>
  authority: string
  authorityValue: Record<SourceIngestGuidance['authorityKey'], string>
  usage: string
  usageValue: Record<SourceIngestGuidance['usageKey'], string>
  next: string
  nextValue: Record<SourceIngestGuidance['nextActionKey'], string>
  recovery: Record<Exclude<SourceIngestGuidance['recoveryAction'], null>, string>
  operationError: Record<'sign-in' | 'service' | 'permission' | 'retry', string>
  technicalDetails: string
  typeOverride: string
  typeOverrideHelp: string
  githubPurposeUnsupported: string
}

const en: SourceIngestCopy = {
  status: 'Source status',
  detectedAs: 'Detected as',
  purpose: 'Purpose',
  suggested: 'Agent suggestion',
  purposeOptions: {
    auto: 'Agent suggestion',
    'design-reference': 'Design reference',
    'content-data': 'Content & data',
    requirements: 'Requirements',
  },
  phase: {
    idle: 'Waiting for a source',
    detected: 'Source detected',
    extracting: 'Extracting source',
    analyzing: 'Analyzing request',
    ready: 'Ready for use',
    'needs-action': 'Action needed',
    error: 'Could not prepare source',
  },
  sourceKind: {
    assistant_prompt: 'Request',
    url_import: 'Web URL',
    ide_file: 'Code file',
    github_pr: 'GitHub pull request',
    figma_frame: 'Figma frame',
    figma_file: 'Figma / OpenPencil file',
    document_file: 'Document',
  },
  provenance: 'Provenance',
  provenancePending: 'Pending verified source readback',
  provenanceValue: {
    prompt: 'Your request in this Studio session',
    url: 'The pasted URL and its fetched readback',
    code: 'A copied file; the original stays unchanged',
    github: 'Repository and pull-request readback',
    'figma-frame': 'Figma URL and validated server-side credential',
    'figma-file': 'Retained original file plus checksum',
    document: 'Retained document plus normalized structure',
  },
  authority: 'Authority',
  authorityValue: {
    instruction: 'The request guides generation; generated output is separate',
    'external-reference': 'Reference only; the external page is never edited',
    'copied-code': 'Imported copy only; the source file is never edited',
    'repository-readback': 'Read-only PR evidence; no repository write',
    'figma-rebuild': 'Studio rebuild, not the original Figma canvas',
    'retained-design': 'Original design remains source-owned until explicit review',
    'normalized-document': 'Structure only; visual fidelity is not claimed',
  },
  usage: 'Used next for',
  usageValue: {
    generate: 'Creating a Library draft from your request',
    reference: 'Grounding a Library draft in fetched page content',
    'review-code': 'Creating a reviewable screen draft from copied code',
    'review-change': 'Preparing a read-only change review',
    'rebuild-design': 'Rebuilding a design-backed draft in the open project',
    'inspect-design': 'Inspecting design facts before explicit generation',
    'inspect-document': 'Reviewing document structure; app generation is not wired yet',
  },
  next: 'Recommended next action',
  nextValue: {
    'add-source': 'Describe the outcome, paste a URL, or attach a file.',
    'review-and-run': 'Confirm the purpose, then continue.',
    wait: 'Keep this page open while Studio finishes.',
    'open-result': 'Open the prepared source or result and review its evidence.',
    'choose-another-file': 'Choose a supported file or use another source type.',
    'connect-figma': 'Connect Figma, then retry without losing this request.',
    'open-project': 'Open a project first so the Figma frame has a destination.',
    retry: 'Retry; your source and request stay attached.',
  },
  recovery: {
    'choose-file': 'Choose another file',
    'connect-figma': 'Connect Figma',
    'open-project': 'Open projects',
    retry: 'Retry',
    'sign-in': 'Sign in again',
  },
  operationError: {
    'sign-in': 'Studio could not retrieve the retained source with this session. Sign in again, reopen the project, and retry.',
    service: 'This workspace cannot reach the source service yet. The source remains unchanged; ask the workspace administrator to enable the DGX source connection.',
    permission: 'Your account cannot retrieve this source. Reopen the project with an account that has source access, then retry.',
    retry: 'Studio could not retrieve the source. The source remains unchanged; retry from this page.',
  },
  technicalDetails: 'Technical details',
  typeOverride: 'Detected the wrong source type?',
  typeOverrideHelp: 'Usually Studio selects the adapter automatically. Change it here only when the detected type is wrong.',
  githubPurposeUnsupported: 'GitHub pull requests can only be used as requirements.',
}

const ko: SourceIngestCopy = {
  ...en,
  status: '소스 상태', detectedAs: '감지한 유형', purpose: '소스 목적', suggested: '에이전트 제안',
  purposeOptions: { auto: '에이전트 제안', 'design-reference': '디자인 참고', 'content-data': '콘텐츠·데이터', requirements: '요구사항' },
  phase: { idle: '소스를 기다리는 중', detected: '소스 감지 완료', extracting: '소스 추출 중', analyzing: '요청 분석 중', ready: '사용 준비됨', 'needs-action': '조치 필요', error: '소스를 준비하지 못함' },
  sourceKind: { assistant_prompt: '요청', url_import: '웹 URL', ide_file: '코드 파일', github_pr: 'GitHub 풀 리퀘스트', figma_frame: 'Figma 프레임', figma_file: 'Figma / OpenPencil 파일', document_file: '문서' },
  provenance: '출처', provenancePending: '검증된 소스 읽기 결과 대기 중', authority: '권한', usage: '다음 사용처', next: '권장 다음 행동',
  provenanceValue: { prompt: '현재 Studio 세션에서 입력한 요청', url: '붙여 넣은 URL과 가져온 읽기 결과', code: '복사한 파일이며 원본은 변경되지 않음', github: '저장소와 풀 리퀘스트 읽기 결과', 'figma-frame': 'Figma URL과 서버에서 확인한 자격 정보', 'figma-file': '보관된 원본 파일과 체크섬', document: '보관된 문서와 정규화된 구조' },
  authorityValue: { instruction: '요청은 생성을 안내하며 생성 결과는 별도로 관리됨', 'external-reference': '참고 전용이며 외부 페이지는 수정하지 않음', 'copied-code': '가져온 복사본만 사용하며 원본 파일은 수정하지 않음', 'repository-readback': 'PR 읽기 전용 증적이며 저장소에 쓰지 않음', 'figma-rebuild': '원본 Figma canvas가 아닌 Studio 재구성 결과', 'retained-design': '명시적으로 검토하기 전까지 원본 디자인이 권한을 가짐', 'normalized-document': '구조만 확인하며 시각적 일치는 주장하지 않음' },
  usageValue: { generate: '요청으로 Library 초안 생성', reference: '가져온 페이지 콘텐츠에 근거한 Library 초안 생성', 'review-code': '복사한 코드로 검토 가능한 화면 초안 생성', 'review-change': '읽기 전용 변경 검토 준비', 'rebuild-design': '열린 프로젝트에서 디자인 기반 초안 재구성', 'inspect-design': '명시적 생성 전에 디자인 사실 검토', 'inspect-document': '문서 구조 검토. 문서 기반 앱 생성은 아직 연결되지 않음' },
  nextValue: { 'add-source': '원하는 결과를 설명하거나 URL을 붙여 넣거나 파일을 추가하세요.', 'review-and-run': '목적을 확인한 뒤 계속하세요.', wait: 'Studio가 끝낼 때까지 이 페이지를 열어 두세요.', 'open-result': '준비된 소스나 결과를 열고 증적을 검토하세요.', 'choose-another-file': '지원되는 파일을 선택하거나 다른 소스 유형을 사용하세요.', 'connect-figma': '요청을 유지한 채 Figma를 연결한 뒤 다시 시도하세요.', 'open-project': 'Figma 프레임이 들어갈 프로젝트를 먼저 여세요.', retry: '소스와 요청을 유지한 채 다시 시도하세요.' },
  recovery: { 'choose-file': '다른 파일 선택', 'connect-figma': 'Figma 연결', 'open-project': '프로젝트 열기', retry: '다시 시도', 'sign-in': '다시 로그인' },
  operationError: { 'sign-in': '현재 로그인으로 보관된 소스를 가져오지 못했습니다. 다시 로그인하고 프로젝트를 연 뒤 재시도하세요.', service: '이 작업공간에서 소스 서비스에 연결할 수 없습니다. 원본은 변경되지 않았습니다. 관리자에게 DGX 소스 연결 활성화를 요청하세요.', permission: '이 계정에는 소스를 가져올 권한이 없습니다. 소스 접근 권한이 있는 계정으로 프로젝트를 다시 연 뒤 재시도하세요.', retry: '소스를 가져오지 못했습니다. 원본은 변경되지 않았습니다. 이 페이지에서 다시 시도하세요.' },
  technicalDetails: '기술 세부 정보',
  typeOverride: '소스 유형이 잘못 감지되었나요?',
  typeOverrideHelp: 'Studio가 보통 자동으로 경로를 선택합니다. 감지 결과가 틀린 경우에만 여기에서 바꾸세요.',
  githubPurposeUnsupported: 'GitHub 풀 리퀘스트는 요구사항으로만 사용할 수 있습니다.',
}

const ja: SourceIngestCopy = {
  ...en,
  status: 'ソースの状態', detectedAs: '検出した種類', purpose: '目的', suggested: 'エージェントの提案',
  purposeOptions: { auto: 'エージェントの提案', 'design-reference': 'デザイン参照', 'content-data': 'コンテンツ・データ', requirements: '要件' },
  phase: { idle: 'ソースを待っています', detected: 'ソースを検出しました', extracting: 'ソースを抽出中', analyzing: 'リクエストを分析中', ready: '使用できます', 'needs-action': '操作が必要です', error: 'ソースを準備できませんでした' },
  sourceKind: { assistant_prompt: 'リクエスト', url_import: 'Web URL', ide_file: 'コードファイル', github_pr: 'GitHubプルリクエスト', figma_frame: 'Figmaフレーム', figma_file: 'Figma / OpenPencilファイル', document_file: 'ドキュメント' },
  provenance: '出典', provenancePending: '検証済みソースの読み取り結果を待機中', authority: '権限', usage: '次の用途', next: '推奨される次の操作',
  provenanceValue: { prompt: 'このStudioセッションで入力したリクエスト', url: '貼り付けたURLと取得した読み取り結果', code: 'コピーされたファイル。元のファイルは変更しません', github: 'リポジトリとプルリクエストの読み取り結果', 'figma-frame': 'Figma URLとサーバーで確認した認証情報', 'figma-file': '保持した元ファイルとチェックサム', document: '保持した文書と正規化された構造' },
  authorityValue: { instruction: 'リクエストは生成を案内し、生成結果は別に管理します', 'external-reference': '参照専用。外部ページは編集しません', 'copied-code': '取り込んだコピーのみ。元ファイルは編集しません', 'repository-readback': 'PRの読み取り専用証跡。リポジトリには書き込みません', 'figma-rebuild': '元のFigmaキャンバスではなくStudioの再構築です', 'retained-design': '明示的なレビューまでは元デザインが権限を保持します', 'normalized-document': '構造のみ。視覚的忠実度は主張しません' },
  usageValue: { generate: 'リクエストからLibrary下書きを作成', reference: '取得したページ内容に基づくLibrary下書きを作成', 'review-code': 'コピーしたコードから確認可能な画面下書きを作成', 'review-change': '読み取り専用の変更レビューを準備', 'rebuild-design': '開いているプロジェクトでデザイン下書きを再構築', 'inspect-design': '明示的な生成前にデザイン情報を確認', 'inspect-document': '文書構造を確認。文書からのアプリ生成はまだ未接続です' },
  nextValue: { 'add-source': '結果を説明するか、URLを貼り付けるか、ファイルを追加してください。', 'review-and-run': '目的を確認して続行してください。', wait: 'Studioが完了するまでこのページを開いたままにしてください。', 'open-result': '準備したソースまたは結果を開き、証跡を確認してください。', 'choose-another-file': '対応ファイルを選ぶか、別のソース種類を使用してください。', 'connect-figma': 'リクエストを保持したままFigmaを接続して再試行してください。', 'open-project': 'Figmaフレームの保存先となるプロジェクトを先に開いてください。', retry: 'ソースとリクエストを保持したまま再試行してください。' },
  recovery: { 'choose-file': '別のファイルを選択', 'connect-figma': 'Figmaを接続', 'open-project': 'プロジェクトを開く', retry: '再試行', 'sign-in': '再ログイン' },
  operationError: { 'sign-in': '現在のセッションでは保持されたソースを取得できません。再ログインしてプロジェクトを開き、再試行してください。', service: 'このワークスペースはソースサービスに接続できません。元のソースは変更されていません。管理者にDGXソース接続の有効化を依頼してください。', permission: 'このアカウントにはソースを取得する権限がありません。アクセス権のあるアカウントで開き直してください。', retry: 'ソースを取得できませんでした。元のソースは変更されていません。このページから再試行してください。' },
  technicalDetails: '技術的な詳細', typeOverride: 'ソース種類の検出が違いますか？', typeOverrideHelp: '通常はStudioが自動選択します。検出が誤っている場合だけ変更してください。', githubPurposeUnsupported: 'GitHubプルリクエストは要件としてのみ使用できます。',
}

const zh: SourceIngestCopy = {
  ...en,
  status: '来源状态', detectedAs: '检测类型', purpose: '用途', suggested: '智能体建议',
  purposeOptions: { auto: '智能体建议', 'design-reference': '设计参考', 'content-data': '内容与数据', requirements: '需求' },
  phase: { idle: '等待来源', detected: '已检测来源', extracting: '正在提取来源', analyzing: '正在分析请求', ready: '可使用', 'needs-action': '需要处理', error: '无法准备来源' },
  sourceKind: { assistant_prompt: '请求', url_import: '网页 URL', ide_file: '代码文件', github_pr: 'GitHub 拉取请求', figma_frame: 'Figma 画框', figma_file: 'Figma / OpenPencil 文件', document_file: '文档' },
  provenance: '来源', provenancePending: '等待已验证的来源读取结果', authority: '权限', usage: '后续用途', next: '建议的下一步',
  provenanceValue: { prompt: '本次 Studio 会话中的请求', url: '粘贴的 URL 与获取结果', code: '文件副本；原文件保持不变', github: '仓库与拉取请求读取结果', 'figma-frame': 'Figma URL 与服务端验证的凭据', 'figma-file': '保留的原文件与校验和', document: '保留的文档与规范化结构' },
  authorityValue: { instruction: '请求用于指导生成，生成结果单独管理', 'external-reference': '仅作参考，不会修改外部页面', 'copied-code': '仅使用导入副本，不会修改原文件', 'repository-readback': '只读 PR 证据，不写入仓库', 'figma-rebuild': '这是 Studio 重建结果，不是原始 Figma 画布', 'retained-design': '明确审核前，原设计保有来源权限', 'normalized-document': '仅确认结构，不声称视觉一致' },
  usageValue: { generate: '根据请求创建 Library 草稿', reference: '根据获取的页面内容创建 Library 草稿', 'review-code': '根据代码副本创建可审核的界面草稿', 'review-change': '准备只读变更审核', 'rebuild-design': '在已打开项目中重建设计草稿', 'inspect-design': '明确生成前检查设计事实', 'inspect-document': '审核文档结构；尚未连接文档生成应用' },
  nextValue: { 'add-source': '描述目标、粘贴 URL 或添加文件。', 'review-and-run': '确认用途后继续。', wait: 'Studio 完成前请保持此页面打开。', 'open-result': '打开已准备的来源或结果并审核证据。', 'choose-another-file': '选择受支持文件或使用其他来源类型。', 'connect-figma': '保留当前请求，连接 Figma 后重试。', 'open-project': '先打开项目，让 Figma 画框有目标位置。', retry: '保留来源和请求并重试。' },
  recovery: { 'choose-file': '选择其他文件', 'connect-figma': '连接 Figma', 'open-project': '打开项目', retry: '重试', 'sign-in': '重新登录' },
  operationError: { 'sign-in': '当前会话无法取回已保留来源。请重新登录、打开项目并重试。', service: '此工作区无法连接来源服务。原来源未被修改；请管理员启用 DGX 来源连接。', permission: '此账号无权取回该来源。请使用有来源权限的账号重新打开项目。', retry: '无法取回来源。原来源未被修改；请在此页面重试。' },
  technicalDetails: '技术详情', typeOverride: '来源类型检测有误？', typeOverrideHelp: 'Studio 通常会自动选择。仅在检测错误时更改。', githubPurposeUnsupported: 'GitHub 拉取请求只能用作需求。',
}

const zhTW: SourceIngestCopy = {
  ...zh,
  status: '來源狀態', detectedAs: '偵測類型', purpose: '用途', suggested: '代理建議',
  purposeOptions: { auto: '代理建議', 'design-reference': '設計參考', 'content-data': '內容與資料', requirements: '需求' },
  phase: { idle: '等待來源', detected: '已偵測來源', extracting: '正在擷取來源', analyzing: '正在分析請求', ready: '可使用', 'needs-action': '需要處理', error: '無法準備來源' },
  sourceKind: { assistant_prompt: '請求', url_import: '網頁 URL', ide_file: '程式碼檔案', github_pr: 'GitHub 拉取請求', figma_frame: 'Figma 畫框', figma_file: 'Figma / OpenPencil 檔案', document_file: '文件' },
  provenance: '來源', provenancePending: '等待已驗證的來源讀取結果', authority: '權限', usage: '後續用途', next: '建議的下一步',
  provenanceValue: { prompt: '本次 Studio 工作階段中的請求', url: '貼上的 URL 與擷取結果', code: '檔案副本；原始檔案維持不變', github: '儲存庫與拉取請求讀取結果', 'figma-frame': 'Figma URL 與伺服器端驗證的憑證', 'figma-file': '保留的原始檔案與校驗碼', document: '保留的文件與正規化結構' },
  authorityValue: { instruction: '請求用於引導生成，生成結果另行管理', 'external-reference': '僅供參考，不會修改外部頁面', 'copied-code': '僅使用匯入副本，不會修改原始檔案', 'repository-readback': '唯讀 PR 證據，不寫入儲存庫', 'figma-rebuild': '這是 Studio 重建結果，不是原始 Figma 畫布', 'retained-design': '明確審核前，原始設計保有來源權限', 'normalized-document': '僅確認結構，不宣稱視覺一致' },
  usageValue: { generate: '根據請求建立 Library 草稿', reference: '根據擷取的頁面內容建立 Library 草稿', 'review-code': '根據程式碼副本建立可審核的畫面草稿', 'review-change': '準備唯讀變更審核', 'rebuild-design': '在已開啟專案中重建設計草稿', 'inspect-design': '明確生成前檢查設計資訊', 'inspect-document': '審核文件結構；尚未連接由文件生成應用程式' },
  nextValue: { 'add-source': '描述目標、貼上 URL 或新增檔案。', 'review-and-run': '確認用途後繼續。', wait: 'Studio 完成前請保持此頁面開啟。', 'open-result': '開啟已準備的來源或結果並審核證據。', 'choose-another-file': '選擇支援的檔案或使用其他來源類型。', 'connect-figma': '保留目前請求，連接 Figma 後重試。', 'open-project': '先開啟專案，讓 Figma 畫框有目標位置。', retry: '保留來源和請求並重試。' },
  recovery: { 'choose-file': '選擇其他檔案', 'connect-figma': '連接 Figma', 'open-project': '開啟專案', retry: '重試', 'sign-in': '重新登入' },
  operationError: { 'sign-in': '目前工作階段無法取回已保留來源。請重新登入、開啟專案並重試。', service: '此工作區無法連接來源服務。原始來源未被修改；請管理員啟用 DGX 來源連線。', permission: '此帳號無權取回該來源。請使用具來源存取權限的帳號重新開啟專案。', retry: '無法取回來源。原始來源未被修改；請在此頁面重試。' },
  technicalDetails: '技術詳情', typeOverride: '來源類型偵測有誤？', typeOverrideHelp: 'Studio 通常會自動選擇。僅在偵測錯誤時更改。',
  githubPurposeUnsupported: 'GitHub 拉取請求只能用作需求。',
}

const copy: Record<LocaleCode, SourceIngestCopy> = {
  en,
  ko,
  ja,
  zh,
  'zh-TW': zhTW,
}

export function getSourceIngestCopy(locale: string): SourceIngestCopy {
  return copy[locale as LocaleCode] ?? en
}
